import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AccessService } from '../../service/access.service';
import { ApiService } from '../../service/api.service';
import { SettingsService } from '../../service/settings.service';
import { UiService } from '../../service/ui.service';

function passwords_match(group: AbstractControl): { [key: string]: boolean } | null {
	const new_password = group.get('new_password');
	const confirm_password = group.get('confirm_password');
	if (new_password && confirm_password && new_password.value !== confirm_password.value) {
		return { passwords_mismatch: true };
	}
	return null;
}

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {

	public loading: boolean = true;
	public user_role: string = 'user';
	public managed_communities: any[] = [];

	public profile_form: FormGroup;
	public password_form: FormGroup;
	public profile_saving: boolean = false;
	public password_saving: boolean = false;

	constructor(
		private fb: FormBuilder,
		public access: AccessService,
		public api: ApiService,
		public s: SettingsService,
		public ui: UiService,
		private router: Router
	) {}

	ngOnInit() {
		this.profile_form = this.fb.group({
			name: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]]
		});

		this.password_form = this.fb.group({
			current_password: ['', Validators.required],
			new_password: ['', [Validators.required, Validators.minLength(6)]],
			confirm_password: ['', Validators.required]
		}, { validators: passwords_match });

		this.access.get_token().then(() => {
			this.api.get_account_info().subscribe((info: any) => {
				if (info) {
					this.user_role = info.role === 'owner' ? 'owner' : 'user';
					this.profile_form.patchValue({
						name: info.name || '',
						email: info.email || ''
					});
					if (this.user_role === 'owner') {
						this.load_managed_communities();
					}
				}
				this.loading = false;
			}, (err) => {
				console.error('Failed to load account info', err);
				// Fall back to JWT data
				if (this.access.user_data) {
					this.profile_form.patchValue({
						email: this.access.user_data.email || ''
					});
				}
				this.loading = false;
			});
		}, (err) => {
			console.error('Token error', err);
			this.loading = false;
		});
	}

	load_managed_communities() {
		this.api.get_managed_communities().subscribe((communities: any) => {
			this.managed_communities = communities || [];
		}, (err) => {
			console.error('Failed to load managed communities', err);
		});
	}

	save_profile() {
		if (this.profile_form.invalid) { return; }
		this.profile_saving = true;
		this.api.update_profile(this.profile_form.value).subscribe(() => {
			this.profile_saving = false;
			this.ui.message('Profile saved.', 'OK', 3000);
		}, (err) => {
			console.error('Profile save failed', err);
			this.profile_saving = false;
			this.ui.message('Failed to save profile.', 'OK', 4000);
		});
	}

	change_password() {
		if (this.password_form.invalid) { return; }
		this.password_saving = true;
		const { current_password, new_password } = this.password_form.value;
		this.api.change_password({ current_password, new_password }).subscribe(() => {
			this.password_saving = false;
			this.password_form.reset();
			this.ui.message('Password changed successfully.', 'OK', 3000);
		}, (err) => {
			console.error('Password change failed', err);
			this.password_saving = false;
			this.ui.message('Failed to change password. Check your current password.', 'OK', 5000);
		});
	}

	get_error(form: FormGroup, field: string): string {
		const control = form.get(field);
		if (!control || !control.errors || !control.touched) { return ''; }
		if (control.errors.required) { return 'This field is required.'; }
		if (control.errors.email) { return 'Enter a valid email address.'; }
		if (control.errors.minlength) { return `Minimum ${control.errors.minlength.requiredLength} characters.`; }
		return '';
	}

	sign_out() {
		this.access.sign_out();
	}

	go_to_community(id) {
		this.router.navigate(['/community', id]);
	}
}
